import { useMemo, useCallback, useEffect, useId, useRef } from 'react';
import { Box, Flex, Icon, PlainButton, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { useNavigateToAgentChat } from '@/features/copilot';
import { useAuth } from '@/services/auth';
import { useServicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useSetupGuide } from '@/features/setup-guide';
import { getPromptsForWorkspace } from '../prompts';
import { buildWorkspaceContext } from '../workspace-context';
import { personalizeCards } from '../personalize-cards';
import { SectionLabel } from './section-label';
import { getCachedCards, setCachedCards, PERSONALIZATION_TTL, type DisplayCard } from './prompt-cards-cache';
import * as styles from './prompt-cards.css';

const queryOptions = { staleTime: 5 * 60_000 } as const;

export function PromptCards({ workspaceId }: { workspaceId: number }) {
  const auth = useAuth();
  const { track } = useAnalytics();
  const enabled = workspaceId > 0;

  const { data: environmentData } = useServicesQuery(
    'getEnvironment',
    { params: { environmentId: workspaceId } },
    { ...queryOptions, enabled },
  );

  const { setupGuideQuery } = useSetupGuide();
  const setupGuideData = setupGuideQuery.data;

  const { data: campaignsData, isError: campaignsError } = useServicesQuery(
    'getCampaigns',
    { params: { environment_id: workspaceId }, query: { page: 0, size: 1 } },
    { ...queryOptions, enabled },
  );

  const { data: segmentsData, isError: segmentsError } = useServicesQuery(
    'getSegments',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: newslettersData, isError: newslettersError } = useServicesQuery(
    'getNewsletters',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: transactionalData, isError: transactionalError } = useServicesQuery(
    'getTransactionalMessages',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: eventsTotalsData, isError: eventsError } = useServicesQuery(
    'getEventNamesTotals',
    { params: { environment_id: workspaceId } },
    { ...queryOptions, enabled },
  );

  const { data: attributesTotalsData, isError: attributesError } = useServicesQuery(
    'getAttributesTotals',
    { params: { environment_id: workspaceId } },
    { ...queryOptions, enabled },
  );

  // Each count query has settled once it either succeeded or errored.
  // When all have settled but counts is still null, we know counts will never
  // arrive (partial or total failure) and should fall back to heuristic cards.
  // When queries are disabled (workspaceId <= 0), treat as settled immediately
  // so we fall back to heuristic cards instead of showing an infinite skeleton.
  const countsSettled =
    !enabled ||
    ((campaignsData != null || campaignsError) &&
      (segmentsData != null || segmentsError) &&
      (newslettersData != null || newslettersError) &&
      (transactionalData != null || transactionalError) &&
      (eventsTotalsData != null || eventsError) &&
      (attributesTotalsData != null || attributesError));

  const workspaceContext = useMemo(
    () =>
      buildWorkspaceContext({
        account: auth.account,
        environmentData: environmentData ?? null,
        setupGuide: setupGuideData?.setup_guide ?? null,
        campaignsData: campaignsData ?? null,
        segmentsData: segmentsData ?? null,
        newslettersData: newslettersData ?? null,
        transactionalData: transactionalData ?? null,
        eventsTotalsData: eventsTotalsData ?? null,
        attributesTotalsData: attributesTotalsData ?? null,
      }),
    [
      auth.account,
      environmentData,
      setupGuideData,
      campaignsData,
      segmentsData,
      newslettersData,
      transactionalData,
      eventsTotalsData,
      attributesTotalsData,
    ],
  );

  // Get a larger pool of heuristic-matched cards for the LLM to rank from
  const candidateCards = useMemo(() => getPromptsForWorkspace(workspaceContext, 8), [workspaceContext]);

  // Heuristic fallback — used if personalization fails or is still loading
  const heuristicCards = useMemo(() => candidateCards.slice(0, 4), [candidateCards]);

  const countsReady = workspaceContext.counts != null;

  // Stable key — changes when the candidate pool changes so the queryFn
  // closure over candidateCards / workspaceContext stays consistent.
  const candidateKey = useMemo(() => candidateCards.map((card) => card.title).join('|'), [candidateCards]);

  const { data: personalizedCards, isFetched: personalizationSettled } = useQuery({
    queryKey: ['personalizePromptCards', workspaceId, candidateKey],
    queryFn: async (): Promise<DisplayCard[]> => {
      const cached = getCachedCards(workspaceId, candidateKey);
      if (cached != null) return cached;

      const result = await personalizeCards({
        cards: candidateCards,
        workspaceContext,
      });

      // Throw on failure so TanStack Query treats it as an error and retries
      // on the next page load instead of caching null for 24 hours.
      if (result == null) {
        throw new Error('Personalization returned no results');
      }

      const display: DisplayCard[] = result.map((item) => ({
        title: item.card.title,
        prompt: item.card.prompt,
        displayTitle: item.title,
        displayDescription: item.description,
      }));

      setCachedCards(workspaceId, candidateKey, display);
      return display;
    },
    enabled: candidateCards.length > 0 && countsReady,
    staleTime: PERSONALIZATION_TTL,
    gcTime: PERSONALIZATION_TTL,
    retry: false,
  });

  // Show skeleton until personalization completes (isFetched covers both success
  // and null returns) or until we know counts will never fully arrive (settled but incomplete)
  const countsFailed = countsSettled && !countsReady;
  const showSkeleton = !personalizationSettled && !countsFailed;
  const displayCards: DisplayCard[] =
    personalizedCards ??
    heuristicCards.map((card) => ({
      title: card.title,
      prompt: card.prompt,
      displayTitle: card.title,
      displayDescription: card.description,
    }));

  const firedRef = useRef<string | null>(null);

  useEffect(() => {
    if (displayCards.length > 0 && !showSkeleton && firedRef.current !== candidateKey) {
      firedRef.current = candidateKey;
      track('home', 'promptCardsShown', {
        Titles: displayCards.map((c) => c.title).join(', '),
        Personalized: personalizedCards != null,
      });
    }
  }, [candidateKey, showSkeleton, displayCards, personalizedCards, track]);

  if (displayCards.length === 0) {
    return null;
  }

  return (
    <Box w="full">
      <Box mb="150">
        <SectionLabel>What should we work on?</SectionLabel>
      </Box>
      <div className={styles.grid}>
        {showSkeleton
          ? Array.from({ length: 4 }, (_, index) => <PromptCardSkeleton key={index} />)
          : displayCards.map((item, index) => <PromptCard key={item.title} card={item} index={index} />)}
      </div>
    </Box>
  );
}

function PromptCardSkeleton() {
  return (
    <div className={styles.card}>
      <Flex alignItems="center" gap="050">
        <SkeletonShape style={{ width: 14, height: 14 }} />
        <SkeletonShape style={{ width: '70%', height: 16 }} />
      </Flex>
      <Box mt="100">
        <SkeletonShape style={{ width: '90%', height: 14 }} />
      </Box>
    </div>
  );
}

const SPARKLE_LARGE_PATH =
  'M15.987 9.36 23 12l-7.013 2.639L13.29 21.5l-2.699-6.861L3.58 12l7.012-2.64L13.29 2.5l2.697 6.86Z';
const SPARKLE_SMALL_TOP_PATH = 'M5.19 6.217H3V4.932h2.19V2.79h1.313v2.142h2.189v1.285h-2.19V8.36H5.19V6.217Z';
const SPARKLE_SMALL_BOTTOM_PATH = 'M5.189 19.068H3v-1.285h2.19V15.64h1.313v2.142h2.189v1.285h-2.19v2.142H5.19v-2.142Z';

function PromptCardSparkle({ gradientId }: { gradientId: string }) {
  return (
    <span className={styles.sparkleWrap} aria-hidden>
      <span className={styles.sparklePluma}>
        <Icon name="sparkle" size="xs" color="subtle" />
      </span>
      <svg className={styles.sparkleGradient} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" style={{ stopColor: themeVars.palette['orange-400'] }} />
            <stop offset="100%" style={{ stopColor: themeVars.palette['purple-500'] }} />
          </linearGradient>
        </defs>
        <path d={SPARKLE_LARGE_PATH} fill={`url(#${gradientId})`} />
        <path d={SPARKLE_SMALL_TOP_PATH} fill={`url(#${gradientId})`} />
        <path d={SPARKLE_SMALL_BOTTOM_PATH} fill={`url(#${gradientId})`} />
      </svg>
    </span>
  );
}

function PromptCard({ card, index }: { card: DisplayCard; index: number }) {
  const navigateToAgentChat = useNavigateToAgentChat();
  const { track } = useAnalytics();

  const handleClick = useCallback(() => {
    track('aiAssistant', 'promptCardClicked', {
      Title: card.title,
      Source: 'home',
      Position: index + 1,
    });

    navigateToAgentChat({ message: card.prompt });
  }, [navigateToAgentChat, card, track, index]);

  const id = useId();
  const gradientId = `prompt-card-sparkle-gradient-${id.replace(/:/g, '')}`;

  return (
    <GradientRing radius={4} display="flex" h="full" w="full">
      <PlainButton className={clsx(styles.card, gradientHoverTarget)} onClick={handleClick}>
        <Flex alignItems="center" gap="050">
          <PromptCardSparkle gradientId={gradientId} />
          <div className={styles.cardTitle}>{card.displayTitle}</div>
        </Flex>
        <div className={styles.cardDescription}>{card.displayDescription}</div>
      </PlainButton>
    </GradientRing>
  );
}
